/**
 * Pure view logic for activations — no React, no network. Ported from rudder-webapp's
 * `syncRunStore` / `syncRunMetrics` / `syncListTable/utils`, which wore mobx decorators over what
 * were already pure functions.
 *
 * This module is the ONE place a raw platform value is narrowed or formatted. Two rules make it
 * load-bearing rather than a convenience:
 *
 * 1. **Never compare a raw run field to a literal.** `sync_type` and `trigger` come back with
 *    unstable casing — the same connection in mini reports `"full"` on one run and `"Full"` on the
 *    next. A direct `run.sync_type === 'full'` renders a full run as "Incremental".
 * 2. **Every number carries its denominator.** `denominated()` exists so no caller has to remember;
 *    a bare count on this surface is a defect, not a style choice.
 */
import { ERROR_DETAILS_RETENTION_FLOOR_DAYS } from '@/audiences/lib/activationSchedule';

import type { MetricGroup, RawSync, RawSyncRun, RunStatus, RunTrigger, RunType, SyncSchedule } from '../types';

/** `"Full"` / `"full"` / undefined → a comparable union. Incremental is the platform default. */
export function normalizeRunType(syncType: string | undefined): RunType {
  return syncType?.toLowerCase() === 'full' ? 'full' : 'incremental';
}

export function normalizeTrigger(trigger: string | undefined): RunTrigger {
  return trigger?.toLowerCase() === 'manual' ? 'manual' : 'scheduled';
}

/**
 * The wire `status` plus the failed-record count, resolved to what actually gets rendered.
 *
 * The final arm was a CATCH-ALL ported verbatim from `syncRunStore.ts:20-34`, which meant an
 * unrecognised future status read as a SUCCESS. That is the one direction this must not fail in, so
 * it is now an explicit `succeeded` check and anything else is `unknown`. Overriding a deliberate
 * pin, decided 2026-08-26 — see `run-view.test.ts`'s `runStatus` describe block for the record.
 */
export function runStatus(run: RawSyncRun): RunStatus {
  const status = run.status.toLowerCase();
  if (status === 'running') return 'running';
  if (status === 'failed') return 'failed';
  if (status !== 'succeeded') return 'unknown';
  return (run.metrics?.failed?.total ?? 0) > 0 ? 'completedWithFailures' : 'completed';
}

/**
 * True when a run has something for the activation failure chat to explain: it failed records, or it
 * failed outright (including a run that stopped before counting any records: the agent explains it
 * from the run's own error text), or its status is unrecognised, which `runSummary` also reports as
 * stopped. A running or clean run is false.
 */
export function runHasFailures(run: RawSyncRun): boolean {
  const status = runStatus(run);
  // A running run's failed count is still moving, so `runStatus` never reads it and it is not a target yet.
  return status === 'failed' || status === 'completedWithFailures' || status === 'unknown';
}

export const RUN_STATUS_LABEL: Record<RunStatus, string> = {
  completed: 'Completed',
  completedWithFailures: 'Completed with failures',
  failed: 'Failed',
  running: 'Running...',
  unknown: 'Unrecognised status',
};

/** A run is live only while the platform says so — never inferred from a missing `finished_at`. */
export function isRunning(run: RawSyncRun | undefined): boolean {
  return run !== undefined && runStatus(run) === 'running';
}

/**
 * Whether this run's metrics are a measurement rather than a placeholder.
 *
 * The payload NEVER omits a key, so "not counted yet" and "genuinely none" are both `0` — on
 * `liveExtract` every metric reads 0 and that same run settles at 1,230. What was actually MEASURED:
 * `metrics.total` reads 0 while `state` is "Creating Unload Data" (t=0) and reads 1,230 by the time
 * `state` is "Sending records" (t=+0.2s) — two polls that span MORE than one phase, so this pins the
 * observation (total goes from 0 to its final value somewhere in that window), not a mechanism.
 * `run-fixtures.ts` reads the same two polls as evidence for a different claim (`sync_type`'s
 * timing) — don't over-read either past what two samples actually show.
 *
 * A settled run is always counted: whatever it says is final, including `settledNothingToDo`'s
 * genuine `changed: 0` against a real `total: 20`.
 *
 * Known edge this signal cannot resolve: a live run over a genuinely empty audience has a true
 * `total` of 0 for its entire life and will read "counting" (never counted) the whole time —
 * indistinguishable, from `metrics.total` alone, from a run that simply hasn't reached the point
 * where total becomes non-zero yet.
 */
export function isCounted(run: RawSyncRun): boolean {
  if (!isRunning(run)) return true;
  return (run.metrics?.total ?? 0) > 0;
}

// Both formatters are pinned to `en` rather than the runtime locale. A bare `toLocaleString()`
// renders 20000 as "20.000" in a de-DE browser — dots where this surface means commas — and every
// number here is read against another number, so a separator that flips meaning is not cosmetic.
const GROUPED = new Intl.NumberFormat('en');
const COMPACT = new Intl.NumberFormat('en', { notation: 'compact' });

/**
 * Compact above 999 (`1.5K`), exact below. Ported from the webapp's `humanizeNumber`.
 *
 * Callers rendering a humanized number MUST also surface the exact value (a tooltip) — the compact
 * form is a reading aid, never the only place the number exists.
 */
export function humanize(n: number): string {
  return Math.abs(n) < 1000 ? GROUPED.format(n) : COMPACT.format(n);
}

/** The exact value, grouped. What a humanized number's tooltip shows. */
export function exact(n: number): string {
  return GROUPED.format(n);
}

/** One decimal, or null when there is no denominator to divide by. */
export function percent(n: number, d: number): string | null {
  if (d <= 0) return null;
  return `${((n / d) * 100).toFixed(1)}%`;
}

/**
 * Whether a count is a count. `metrics.changed.total === -1` is the platform's sentinel for "the
 * count query timed out" — it is not a quantity, so it must never be formatted, divided by, or
 * summed. Zero IS measured; only a negative is the sentinel.
 */
export function isMeasured(n: number): boolean {
  return n >= 0;
}

/**
 * The words every caller says instead of a denominator when `denominated()`'s `of` is null — the
 * `-1` sentinel means there is genuinely nothing to divide by or print. This module's header calls
 * itself "the ONE place a raw platform value is narrowed or formatted"; a literal repeated at each
 * call site (`RunDenominated` at both ends of its fraction, `syncedOfChanged` in `activation-list.ts`,
 * `runSummary`'s three sentences below) would be several strings that can drift apart with nothing
 * enforcing they stay identical, so they all import this one instead.
 */
export const PLAN_UNMEASURED = 'plan size unmeasured';

/**
 * The parts of `1,544 of 20,000 users · 7.7%`. Returned as parts rather than a string so a caller
 * can style the pieces — a caller that renders `value` alone, with neither `of` nor the words
 * {@link PLAN_UNMEASURED} in its place, has produced a bare number, which this surface does not
 * permit. `of === null` is the one case where dropping the denominator is correct: there is nothing
 * to state, so the caller states that in words instead of printing the sentinel.
 *
 * `of` is null when the denominator is the `-1` sentinel. `percent()` already guards `d <= 0`, but
 * it returned null silently while the denominator went on rendering as "-1".
 */
export function denominated(
  n: number,
  d: number,
  unit: string,
): { value: string; of: string | null; unit: string; percent: string | null } {
  if (!isMeasured(d)) return { value: exact(n), of: null, unit, percent: null };
  return { value: exact(n), of: exact(d), unit, percent: percent(n, d) };
}

/** `metrics.total − metrics.invalid.total` — the denominator the source side reports against. */
export function validRecords(run: RawSyncRun): number {
  return (run.metrics?.total ?? 0) - (run.metrics?.invalid?.total ?? 0);
}

/** A metric group that is absent reads as all-zero rather than blanking the row. */
export function group(g: MetricGroup | undefined): Required<Pick<MetricGroup, 'total'>> & MetricGroup {
  return { total: g?.total ?? 0, ...g };
}

/**
 * The records a run planned to move that have no recorded outcome:
 * `changed − (successful + failed + dropped)`.
 *
 * The platform has no name for this and never reports it — it only exists if you compute it. The
 * SAME arithmetic means two opposite things and only `status` separates them: mid-run it is work in
 * flight, and on a run that died it is records abandoned. Callers must pick the word from `status`;
 * this function deliberately returns a bare number and takes no position.
 *
 * `null` in two cases, both meaning "there is nothing honest to subtract yet or from":
 *  - the plan size is the `-1` sentinel — there is no plan size, so there is no shortfall to compute;
 *  - `!isCounted(run)` — a live run that hasn't reached its first real `metrics.total` reads `changed`
 *    as `0`, same as a genuinely balanced settled run (`settledCleanFull`). Without this check the two
 *    are indistinguishable and `liveExtract`/`livePreparing`/`liveStateEmpty` would each report an
 *    honest-looking `0` gap for a run that has measured NOTHING — exactly the "0 asserted as a
 *    measurement" defect `isCounted` exists three functions above to name. (In practice this path is
 *    unreachable from `deliveryOutcome`, which already returns `pending` for any running run before
 *    calling this — but `unaccounted` is exported and reviewed on its own contract, not only through
 *    that one caller, so it must refuse the lie itself rather than rely on a caller to dodge it.)
 *
 * Two independent things produce a POSITIVE value, and they are not distinguishable here:
 *  - a run stopped or failed mid-send keeps its full plan with zero delivery;
 *  - UNVERIFIED, no fixture — a destination abort whose HTTP status is neither 400 nor 401 is written
 *    to NO metric at all (`getPgMetricType` returns an empty type and `SaveMetrics` skips the write),
 *    which can open a silent gap on a run that reports `succeeded`. Read from rudder-server source,
 *    not observed on the wire — same evidentiary status as `runSummary`'s "INFERRED, not observed"
 *    note on the plan-build-failure branch.
 *
 * NEGATIVE is arithmetically reachable and is NOT clamped. `successful: 200` against `changed: 100`
 * returns `-100` — more landed than was ever planned. Unobserved (the identity held on all 30 runs
 * surveyed, and broke only in the positive direction, on `failedStoppedMidSend`) and probably
 * unreachable, but "unobserved" is not "impossible" and this module guards several equally-unobserved
 * shapes. It is returned as-is on purpose: a negative gap is a CONTRADICTION in the payload, not a
 * quantity of records, and clamping it to `0` would erase the one signal that says so — the same
 * mistake as formatting the `-1` sentinel. Two consequences a caller must know:
 *  - `deliveryOutcome` tests `gap > 0`, so a negative gap classifies as `refused`/`accepted` on
 *    `successful` alone — it never reads as `abandoned`;
 *  - a caller that RENDERS this number (Stage 3's shortfall line) must refuse to print a negative one,
 *    exactly as `denominated()` refuses to print the `-1` denominator. There is no honest sentence
 *    containing "-100 records unaccounted for".
 */
export function unaccounted(run: RawSyncRun): number | null {
  if (!isCounted(run)) return null;
  const changed = run.metrics?.changed?.total ?? 0;
  if (!isMeasured(changed)) return null;
  const landed =
    (run.metrics?.successful?.total ?? 0) + (run.metrics?.failed?.total ?? 0) + (run.metrics?.dropped?.total ?? 0);
  return changed - landed;
}

/**
 * What actually became of a run's plan, as one word. THE classifier for this surface.
 *
 * The predicate this replaced (`hasNoRecordsSynced`, since deleted — nothing calls it) could not tell
 * these apart: it fired identically for a destination that refused all 176 records (a gap with an
 * OWNER — the far side said no) and a sync stopped mid-send with 1,230 records and no recorded outcome
 * at all (a gap with NO owner). One of those has someone to talk to and the other does not, and they
 * were painted the same red.
 *
 * `unmeasured` is checked FIRST, ahead of `nothingToDo` — review round 3, IMPORTANT 1. The two are
 * opposite facts fused into one word by the original `!isMeasured(changed) || changed === 0` guard:
 * `settledPlanUnmeasured` (`changed.total === -1`) ACCEPTED 890 records, and the fused version called
 * that run `nothingToDo` — the only arm in this function that shadowed another, swallowing a run
 * before `refused`/`abandoned`/`accepted` ever saw it.
 *
 * ── THE SEAM ────────────────────────────────────────────────────────────────
 * This function decides what a run IS; {@link runSummary} decides only what to CALL it, by switching
 * over this word. That split is the fix for a real defect, not tidiness: `runSummary` used to
 * re-derive the same distinctions with its own arms, in a different order and under different
 * conditions (`changed === 0 && total > 0` there against `changed === 0` here; the `-1` sentinel
 * checked after the empty-plan case there and before it here). The two divergences were exactly the
 * two payloads where the sentence lied — a run the platform called `failed` with `changed: 0,
 * total: 20` rendered "Nothing to sync" beside a red badge, and a `succeeded` run with
 * `total: 0, changed: 0` fell through every guard to "0 / 0 records accepted.", the one string that
 * branch existed to delete. One classifier cannot disagree with itself.
 *
 * This side is blind to the FAILED/SUCCEEDED/UNKNOWN distinction specifically — not to status
 * outright. The first line is `isRunning(run)`, which is `runStatus(run) === 'running'`, so the
 * `pending` arm is pure status; live and settled is a split this function must make, because a
 * mid-flight gap and an abandoned one are the same arithmetic. What it must NOT do is distinguish a
 * settled run's outcome by how the platform labelled it: `RunsPanel`'s delta column keys severity off
 * this word alone, precisely so a `refused` run's tint matches a badge derived separately from
 * `runStatus`. That agreement is narrower than it looks — it holds only when the destination's
 * rejection is also written to `metrics.failed` (`settledDestinationRefused`: 176/176 failed, which
 * `runStatus` promotes to `completedWithFailures`). A run with `changed > 0, successful 0, failed 0,
 * dropped === changed` is ALSO `refused` here while `runStatus` reads straight through to `completed`.
 * Whether `refused` should require `failed > 0` is a vocabulary question, still open. Where the SENTENCE depends on that distinction — a run that
 * DIED reports where it stopped, not what it achieved — `runSummary` reads `runStatus(run)` itself, at
 * the wording layer.
 *
 * Two things this word deliberately does not carry. Both are recoverable at the wording layer from
 * functions above, and neither is a distinction a severity consumer wants:
 *  - `pending` spans a live run's whole life, including the extract half where nothing has been
 *    counted yet. `isCounted(run)` separates them ("Counting records." vs "N of M accepted so far.").
 *  - `nothingToDo` says the PLAN was empty, not that the payload was. `total > 0` (`settledNothingToDo`
 *    — the modal run, a real audience whose membership did not move) and `total === 0` (an audience
 *    query that returned no rows) are the same outcome and two different sentences.
 */
export function deliveryOutcome(
  run: RawSyncRun,
): 'accepted' | 'refused' | 'abandoned' | 'nothingToDo' | 'unmeasured' | 'pending' {
  if (isRunning(run)) return 'pending';

  const changed = run.metrics?.changed?.total ?? 0;
  if (!isMeasured(changed)) return 'unmeasured';
  if (changed === 0) return 'nothingToDo';

  // `gap` cannot actually BE null here: the `unmeasured` arm above already returned if `changed`
  // were the `-1` sentinel, and `run` is guaranteed not running (the `pending` arm above already
  // returned), so `isCounted(run)` is unconditionally true too — both of `unaccounted`'s null paths
  // are excluded before this line runs. The `!== null` check below is therefore RUNTIME-dead, not
  // TypeScript-dead: `unaccounted`'s return type is `number | null` regardless of what this call site
  // can prove about its own inputs, and this repo's lint runs `no-non-null-assertion` at a level
  // `--max-warnings=0` fails on, so a `!` assertion is not the cheaper option — an explicit, narrowing
  // check is. Kept for the compiler, not because the branch is reachable.
  const gap = unaccounted(run);
  if (gap !== null && gap > 0) return 'abandoned';

  const successful = run.metrics?.successful?.total ?? 0;
  return successful === 0 ? 'refused' : 'accepted';
}

/**
 * The canonical one-line prose for a run — the callout description, and the sub-line of every
 * past-runs row.
 *
 * WORDING ONLY. Every distinction between runs is made by {@link deliveryOutcome} (see its SEAM
 * section); this function switches over that word and adds exactly two things it cannot carry:
 * `isCounted(run)`, which splits a live run's counting half from its delivering half, and
 * `runStatus(run)`, which decides whether a settled plan is reported as an OUTCOME ("accepted") or as
 * where the run stopped ("Stopped after"). A run that died did not finish its plan, so its totals are
 * not an achievement; `unknown` is grouped with `failed` for the same reason (Task 7 stopped
 * `runStatus` catch-all'ing an unrecognised status to `completed`, and an unrecognised status is no
 * more evidence of an accepted delivery than `failed` is). The `switch` is exhaustive on purpose: a
 * new outcome word forces a wording decision here rather than silently taking a default branch.
 *
 * Ported from `syncRunStore.ts:36-48`, but no longer verbatim. The port asserted
 * "N / M records synced successfully." for EVERY run, which produced
 * "0 / 1.2K records synced successfully." underneath a callout saying the sync was cancelled
 * (`failedStoppedMidSend`), and "0 / 0 records synced successfully." about a run that had not
 * counted anything yet (`liveExtract`). Both are claims the payload does not support.
 *
 * "Accepted", not "synced successfully": rudder-server's stats_collector counts Filtered alongside
 * Succeeded, so `metrics.successful` includes records deliberately never sent.
 *
 * `changed === 0` is read differently by status, and that difference is not cosmetic. On a settled
 * `succeeded` run it is a MEASUREMENT — the run completed and reported an empty plan, which is the
 * modal outcome (`settledNothingToDo`: 16 of 27 runs surveyed). On a run that DIED, the same `0` is
 * not a completed measurement of anything: the run's counters are where it stopped, and nothing
 * distinguishes "the plan was empty" from "the plan was never finished being built". Saying "Nothing
 * to sync" about it — beside a red Failed badge and an error callout — asserts a health the payload
 * cannot support. Hence two sentences off one `nothingToDo`.
 *
 * Deliberately NOT claimed here: which phase computes `changed`, or that a failed run's 0 proves the
 * diff never ran. `isCounted`'s doc above is explicit that the two polls behind that story span more
 * than one phase and pin an observation rather than a mechanism — the argument above needs only the
 * weaker premise (a died run's counters are not a completed measurement), so it does not borrow the
 * stronger one.
 *
 * `changed.total === -1` is the count-query-timeout sentinel (`isMeasured`, `denominated()`,
 * `PLAN_UNMEASURED` — all in this module). This is the ONE other place a raw `-1` could reach the
 * screen: `RunDenominated` already refuses to print it, at both ends of its fraction, and this sentence must
 * refuse it too — in EVERY branch that interpolates `changed`, not only the settled one. That refusal
 * now rides on `deliveryOutcome`'s `unmeasured` arm rather than on a per-branch `isMeasured` check, so
 * no future branch can forget it. The guard asserts nothing about how often the sentinel reaches a
 * running or failed row (no fixture has captured that combination); it only refuses to format a value
 * that is not a number.
 *
 * INFERRED, not observed: `changed.total` is the plan's own count, so a timed-out count query
 * plausibly leaves `-1` in place before delivery even starts, and nothing recomputes it once a run
 * is later stopped or fails. `run-fixtures.ts`'s doc on `settledPlanUnmeasured` states only that
 * `-1` means "the count query timed out" (its `basis`) and that no negative value has ever been
 * observed (its `unverified`) — it does NOT say when in a run's life that timeout occurs, so the
 * claim above is a plausible mechanism, not a confirmed one. Either way, "unobserved" is not
 * "impossible" (see `run-fixtures.ts`'s own framing of its constructed fixtures), so each branch
 * below states {@link PLAN_UNMEASURED} in place of `changed` rather than rendering e.g. "Stopped
 * after 0 of -1 records."
 *
 * `dropped` is appended to EVERY branch, not only the settled ones. It is measured independently of
 * `changed` and of `status`, so a failed run carrying `dropped: 72` used to render "Stopped after 500
 * of 1.2K records." with those 72 records named on no screen at all — this surface's own
 * records-on-no-screen defect, in the status where losing records matters most.
 */
export function runSummary(run: RawSyncRun): string {
  const total = run.metrics?.total ?? 0;
  const successful = run.metrics?.successful?.total ?? 0;
  const changed = run.metrics?.changed?.total ?? 0;
  const dropped = run.metrics?.dropped?.total ?? 0;
  const droppedSentence = dropped > 0 ? ` ${humanize(dropped)} records dropped in data transformation.` : '';
  // `stopped` is the wording layer's ONE use of status — see this function's doc. It is read here
  // rather than inside `deliveryOutcome` so the classifier stays status-blind for its severity
  // consumers.
  const status = runStatus(run);
  const stopped = status === 'failed' || status === 'unknown';

  switch (deliveryOutcome(run)) {
    case 'pending':
      // `isCounted` is false only while running, so this is the only arm that can ask it. Every metric
      // reads 0 during the extract half and the same run settles at 1,230 — there is no number here
      // that is safe to say yet.
      if (!isCounted(run)) return 'Counting records.';
      return isMeasured(changed)
        ? `${humanize(successful)} of ${humanize(changed)} records accepted so far.${droppedSentence}`
        : `${humanize(successful)} records accepted so far; ${PLAN_UNMEASURED}.${droppedSentence}`;

    case 'nothingToDo':
      // `total === 0` is a DIFFERENT fact from an empty plan against a real audience, and it is the
      // more reachable of the two on a succeeded run: an audience query that returns zero rows is an
      // ordinary thing. "0 of 0 records changed." states a denominator that is not one.
      if (total === 0) {
        return stopped
          ? `Stopped before any records were counted.${droppedSentence}`
          : `Nothing to sync — the audience returned no records.${droppedSentence}`;
      }
      return stopped
        ? `Stopped before any changes were identified — ${humanize(changed)} of ${humanize(total)} records.${droppedSentence}`
        : `Nothing to sync — ${humanize(changed)} of ${humanize(total)} records changed.${droppedSentence}`;

    case 'unmeasured':
      return stopped
        ? `Stopped after ${humanize(successful)} records; ${PLAN_UNMEASURED}.${droppedSentence}`
        : `${humanize(successful)} records accepted; ${PLAN_UNMEASURED}.${droppedSentence}`;

    // A measured, non-empty plan. The three words differ in WHO owns the gap, which is a severity
    // question rather than a wording one — this sentence states the same two numbers for all three,
    // and the callout/badge beside it carries the severity.
    case 'accepted':
    case 'refused':
    case 'abandoned':
      return stopped
        ? `Stopped after ${humanize(successful)} of ${humanize(changed)} records.${droppedSentence}`
        : `${humanize(successful)} of ${humanize(changed)} records accepted.${droppedSentence}`;
  }
}

const DURATION_UNITS: [seconds: number, suffix: string][] = [
  [365 * 24 * 3600, 'yr'],
  [30 * 24 * 3600, 'mo'],
  [24 * 3600, 'd'],
  [3600, 'h'],
  [60, 'm'],
  [1, 's'],
];

/**
 * Compact duration, at most two units: `2m 41s`, `1d 13h`, `0s`.
 *
 * Reimplemented rather than ported — the webapp's `readableDurationSeconds` reaches into
 * `moment.duration(...)._data`, a private field.
 */
export function formatDurationSeconds(totalSeconds: number): string {
  const seconds = Math.max(0, Math.floor(totalSeconds));
  if (seconds === 0) return '0s';

  const parts: string[] = [];
  let remaining = seconds;
  for (const [size, suffix] of DURATION_UNITS) {
    if (parts.length === 2) break;
    const value = Math.floor(remaining / size);
    if (value > 0 || parts.length > 0) {
      if (value > 0) parts.push(`${value}${suffix}`);
      remaining -= value * size;
    }
  }
  return parts.length > 0 ? parts.join(' ') : '0s';
}

/**
 * A run's elapsed seconds. `now` is a required parameter, not `Date.now()` inside — a live run
 * re-renders every second and the caller owns that tick, so this stays pure and testable.
 */
export function runDurationSeconds(run: RawSyncRun, now: number): number {
  const started = Date.parse(run.started_at);
  if (Number.isNaN(started)) return 0;
  const end = isRunning(run) || !run.finished_at ? now : Date.parse(run.finished_at);
  return Math.max(0, ((Number.isNaN(end) ? now : end) - started) / 1000);
}

/**
 * The platform's default run retention. A DEFAULT, not a fact: retention is per-connection config
 * (`config.source.syncSettings.syncLogsConfig.logRetentionInDays`) and the editor lets it be set to
 * other values. The server's own `RUN_WINDOW_DAYS` (`src/rpc/procedures/activations.ts`) keeps this
 * value as the query default.
 */
export const RUN_RETENTION_DAYS = 30;

/**
 * This connection's run-log retention: a whole number of days, or `null` when the connection has
 * explicitly turned run-log retention OFF.
 *
 * `syncLogsConfig.enabled === false` is the wizard's "Do not retain logs" option
 * (`ActivationSyncSchedule.tsx`'s "Retain sync logs?" control, subtitle "RudderStack will retain
 * execution logs for a set duration") — `buildConnectionSourceConfig`
 * (`services/audience-rudderstack.ts`) writes `enabled: false` while STILL writing whatever
 * `logRetentionInDays` was last in the form, so reading the number alone (as this function used to)
 * would state a retention period for a connection that keeps none. `enabled` wins over the number.
 *
 * `enabled: undefined` (absent) reads as ON, same convention as `DestinationRef.enabled` elsewhere in
 * this module's neighbourhood — Lookout's own wizard always writes it explicitly, but a connection
 * built or edited outside Lookout may omit the key, and an absent field is not the same claim as an
 * explicit `false`.
 *
 * `null` is not "unknown" — it is "there is nothing honest to state a duration for". A caller that
 * needs an actual number for query/calendar math (there is no separate "disabled" query shape) should
 * fall back to `RUN_RETENTION_DAYS` itself rather than treating `null` as a number.
 *
 * This function reads the connection's OWN config and nothing else.
 */
export function retentionDays(sync: RawSync | undefined): number | null {
  const logs = sync?.config?.source?.syncSettings?.syncLogsConfig;
  if (logs?.enabled === false) return null;
  const configured = logs?.logRetentionInDays;
  return typeof configured === 'number' && configured > 0 ? configured : RUN_RETENTION_DAYS;
}

/**
 * How long this connection keeps each failed record's error now ("Retain and show failures",
 * `syncSettings.errorDetailsConfig`): days, or `null` when it keeps none. Absent config is off, as
 * rudder-sources reads it, a stored 0 (or no number) is the 30-day default, and any other number
 * below the platform floor reads at the floor (`readConnectionSyncSettings` on the server makes the
 * same calls). `undefined` while the sync
 * has not arrived.
 */
export function failuresRetentionDays(sync: RawSync | undefined): number | null | undefined {
  if (sync === undefined) return undefined;
  const config = sync.config?.source?.syncSettings?.errorDetailsConfig;
  if (config?.enabled !== true) return null;
  const days = config.retentionInDays;
  // 30 is rudder-sources' default when the switch is on with no number, or with a stored 0
  // (`ERROR_DETAILS_DEFAULT_RETENTION_DAYS` / `errorDetailsRetentionDays` on the server).
  const whole = Math.floor(typeof days === 'number' ? days : 0);
  return whole === 0 ? 30 : Math.max(ERROR_DETAILS_RETENTION_FLOOR_DAYS, whole);
}

/**
 * True when a sync only ever runs because somebody pressed the button.
 *
 * Derived from the schedule's OWN SHAPE, never from a display string. This predicate feeds
 * `scheduleKind`, which is what the Schedule filter matches on — so deriving it by comparing against
 * a label would mean any wording change silently emptied the filter, with nothing on screen to say
 * so. The three conditions are the ones the labels below short-circuit on, in the same order.
 */
export function isManualSchedule(schedule: SyncSchedule | undefined): boolean {
  const every = schedule?.every ?? 0;
  return !schedule || schedule.type?.toLowerCase() === 'manual' || every <= 0;
}

/**
 * The count and its already-pluralised unit, shared by both labels so they can never disagree about
 * hours-versus-minutes — the one distinction a truncated cadence destroys.
 *
 * `every` is a count IN `schedule.unit`, and reading it without the unit is how this surface used to
 * state a cadence 60× off. Lookout's own wizard always writes `minutes`, so every row we create round
 * -tripped correctly and the bug was invisible from anything we could build — but the list renders
 * every sql-audience source in the workspace, including webapp-built ones, and those pick their own
 * unit. `{unit: 'hours', every: 12}` rendered `12 minutes`; worse, `{unit: 'hours', every: 60}` hit
 * the `% 60` collapse and rendered a confident, plausible `1 hour` for a sync that runs every 60.
 *
 * So the unit is read, and the cadence is stated in the unit the platform stored it in. The one
 * rewrite kept is the readability collapse of exact hours' worth of minutes — 1440 reads as
 * `24 hours` — and it is now correctly confined to rows actually measured in minutes. 90 minutes
 * still stays `90 minutes`, because `1.5 hours` is a number this surface cannot make exact.
 *
 * An unrecognised unit is echoed rather than assumed. The editor refuses those (`days` and `seconds`
 * are real and do not convert honestly; anything else is unknown), but refusing to EDIT a sync is not
 * a reason to misdescribe it in a list — and a wrong number is worse than an unfamiliar word.
 */
function cadence(schedule: SyncSchedule): { count: number; unit: string } {
  const every = schedule.every ?? 0;
  const unit = (schedule.unit ?? 'minutes').trim().toLowerCase();
  const plural = (word: string, count: number): string => (count === 1 ? word : `${word}s`);

  if (unit === 'minutes' || unit === 'minute') {
    const useHours = every > 0 && every % 60 === 0;
    return useHours
      ? { count: every / 60, unit: plural('hour', every / 60) }
      : { count: every, unit: plural('minute', every) };
  }
  // Already singular or plural on the wire depending on who wrote it; normalise before pluralising so
  // `hours` with a count of 1 does not render `1 hours`.
  const singular = unit.endsWith('s') ? unit.slice(0, -1) : unit;
  return { count: every, unit: plural(singular, every) };
}

/**
 * The cadence, rendered whole. `Syncs every 24 …` with a truncated unit is the same class of defect
 * as a number without its denominator, so the unit is always present and always pluralised.
 *
 * This is the full sentence: the card renders it, and the table cell carries it as a `title`.
 */
export function scheduleLabel(schedule: SyncSchedule | undefined): string {
  // `isManualSchedule` cannot narrow `schedule` for TypeScript, so the undefined case stays explicit.
  if (schedule === undefined || isManualSchedule(schedule)) return 'Manual';

  const { count, unit } = cadence(schedule);
  const at = schedule.start_at ? ` at ${schedule.start_at}` : '';
  return `Syncs every ${count} ${unit}${at}`;
}

/**
 * The cadence for a narrow column: `24 hours`, `30 minutes`, `Manual`.
 *
 * The leading verb is what pushed the full sentence out of its cell. The column is headed "Schedule",
 * so "Syncs every" restated the header on every row and charged the unit for the space. The start
 * time drops here too; both survive in the cell's `title`, which is the rule the rest of this file
 * already follows — a shortened value must surface its exact form somewhere the reader can reach.
 */
export function scheduleShortLabel(schedule: SyncSchedule | undefined): string {
  if (schedule === undefined || isManualSchedule(schedule)) return 'Manual';

  const { count, unit } = cadence(schedule);
  return `${count} ${unit}`;
}
